import {
    animate,
    state,
    style,
    transition,
    trigger,
} from "@angular/animations";
import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import {
    AfterContentChecked,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    OnDestroy,
    OnInit,
    Output,
    PLATFORM_ID,
    ViewChild,
} from "@angular/core";
import { makeStateKey, TransferState } from "@angular/platform-browser";
import { Router } from "@angular/router";

import {addDays, addMinutes, format, isBefore, isEqual} from "date-fns";
import diffInMinutes from "date-fns/differenceInMinutes";

import { Subscriber } from "rxjs";

import { ModalService } from "pf_styleguide";
import { ApiEndpoints } from "../shared/apiEndpoints";
import { PitchData } from "../shared/model/pitch";
import { Slot } from "../shared/model/slot";
import { SlotCollection } from "../shared/model/slotCollection";
import { CheckoutPopupOpenInterface } from "../shared/module/checkout-popup/checkout.popup.open.interface";
import { VenueService } from "../venue/venue.service";

const ONE_SLOT_PX = 80.75;
const SLOTCOLLECTION = makeStateKey("slotCollection");

@Component({
    selector: "mlp-calendar",
    templateUrl: "./calendar.component.html",
    styleUrls: ["./calendar.component.scss"],
    animations: [
        trigger("moveCalendarContent", [
            state("*", style({})),
            transition("* <=> *", [animate("2s linear")]),
        ]),
    ],
})
export class CalendarComponent
    implements OnInit, AfterContentChecked, OnDestroy
{
    state = "any";
    cutOffPointAmPmInPx = 0;
    initUlPosition = 0;
    nextWeekEmpty = false;
    allowClick = false;
    timeCollection = [];
    diffDurationsForTime = {};
    dayTimeDuration = {};
    dayTimeSlots = {};
    numberOfRecurringTimesInDay = {};
    numberOfDaysToCollect = 6;
    slotCollection: SlotCollection;
    days: Array<{
        date: Date;
        slots: Array<Slot>;
    }> = [];
    isBrowser: boolean = isPlatformBrowser(this.platformId);

    openCheckoutPopup: EventEmitter<CheckoutPopupOpenInterface> =
        new EventEmitter();

    @Output() moveRightEvent = new EventEmitter();
    @Output() allowClickEvent = new EventEmitter();

    // communication between draggable container
    @Output() ulCurrentPosition = 0;
    @Output() ulMaxPosition = 0;
    @Output() forceUlPositionEvent = new EventEmitter();
    @Output() tellCurrentUlPosition = new EventEmitter();
    @Output() triggerSetupEvent: EventEmitter<any> = new EventEmitter();
    private init = true;
    private readonly subscribed: Subscriber<any>;

    // default min and max time
    private minTimeStep = 60;
    private maxTotalSlots: number;
    private currentDate: Date = new Date();
    private readonly now: Date = new Date();

    constructor(
        @Inject(PLATFORM_ID) private readonly platformId,
        @Inject(DOCUMENT) private readonly document,
        public modalService: ModalService,
        public venueService: VenueService,
        private readonly apiEndpoints: ApiEndpoints,
        private readonly router: Router,
        private readonly transferState: TransferState,
        private readonly el: ElementRef
    ) {
        const slotCollection = this.transferState.get(
            SLOTCOLLECTION,
            undefined
        );
        if (slotCollection) this.slotCollection = slotCollection;


        // subscribe when url changes
        this.subscribed = this.venueService.pitchDataChange.subscribe(
            (data: PitchData) => {
                this.calendarInit(data.data.id, new Date(data.data.attributes.booking_window_min));
            }
        );
    }

    ngOnInit(): void {
        if (this.venueService.pitchData) {
            const data = this.venueService.pitchData;
            this.calendarInit(data.data.id, new Date(data.data.attributes.booking_window_min));
        }

        this.allowClickEvent.subscribe((allow: boolean) => {
            this.allowClick = allow;
        });

        this.tellCurrentUlPosition.subscribe((px: number) => {
            this.ulCurrentPosition = px;
        });

        this.triggerSetupEvent.subscribe(() => {
            this.forceUlPositionEvent.emit(this.cutOffPointAmPmInPx);
        });
    }

    ngAfterContentChecked(): void {
        if (this.isBrowser) {
            if (this.init) {
                const element =
                    this.el.nativeElement.querySelector(".scroll-container");

                if (element) {
                    this.calculateMaxPosition(element);

                    this.init = false;
                }
            }
        }
    }

    ngOnDestroy(): void {
        // required so that when url changes and calendar is gone we dont keep it
        this.subscribed.unsubscribe();
    }

    nextWeek(days: number): void {
        if (days <= 0 || (days > 0 && !this.nextWeekEmpty)) {
            const futureOrPast = addDays(
                new Date(this.slotCollection.meta.filter.starts),
                days
            );
            this.currentDate = isBefore(futureOrPast, this.now)
                ? this.now
                : futureOrPast;
            this.calendarInit(
                this.venueService.pitchData.data.id,
                this.currentDate
            );
        }
    }

    scrollToEnquiry(): void {
        this.document.getElementById("mlp-enquiry").scrollIntoView();

        // adjust fixed header margin
        window.scrollBy(0, -55);
    }

    firstDate(): void {
        const data = this.venueService.pitchData;
        this.currentDate = new Date(data.data.attributes.booking_window_min);
        this.calendarInit(
            data.data.id,
            this.currentDate
        );
    }

    moveLeft(): void {
        this.animateContent();
        this.moveRightEvent.emit(100);
    }

    moveRight(): void {
        this.animateContent();

        if (this.ulCurrentPosition <= this.ulMaxPosition + 100) {
            this.forceUlPositionEvent.emit(this.ulMaxPosition);
        } else {
            this.moveRightEvent.emit(-100);
        }
    }

    isBeforeOrEqualDate(): boolean {
        return !!(
            isBefore(this.currentDate, this.now) ||
            isEqual(this.now, this.currentDate)
        );
    }

    before5PM(): void {
        this.animateContent();
        this.forceUlPositionEvent.emit(0);
    }

    after5PM(): void {
        this.animateContent();
        this.forceUlPositionEvent.emit(this.initUlPosition);
    }

    slotClick(slot: Slot): void {
        if (slot.attributes.availabilities > 0 && this.allowClick) {
            this.openCheckoutPopup.emit({
                slot,
                pitchData: this.venueService.pitchData,
                venueData: this.venueService.venueData,
            });
        }
    }

    private animateContent(): void {
        this.state = this.state === "any" ? "other" : "any";
    }

    private calculateMaxPosition(element): void {
        const containerWidth = element.offsetWidth;

        const result = containerWidth / ONE_SLOT_PX;
        let diff = this.maxTotalSlots - result;

        if (diff < 0) {
            diff = 0;
        }

        this.ulMaxPosition = diff * ONE_SLOT_PX * -1;
    }

    private calendarInit(pitchId: number, date: Date): void {
        this.timeCollection = [];
        this.diffDurationsForTime = {};
        this.dayTimeDuration = {};
        this.dayTimeSlots = {};
        this.numberOfRecurringTimesInDay = {};

        this.apiEndpoints
            .callPitchSlots(pitchId, date)
            .subscribe((slotCollection: SlotCollection) => {
                this.slotCollection = slotCollection;
                this.createHourDurationObj(slotCollection);
                this.findMinTimeStep(slotCollection);
                this.populateDaysWithSlots(slotCollection);
                this.maxTotalSlots = this.days[0].slots.length;
                this.cutOffPointAmPmInPx = this.calculateCutOffPointFor12PM();

                if (this.isBrowser) {
                    // max Position
                    const element =
                        this.el.nativeElement.querySelector(
                            ".scroll-container"
                        );

                    if (element) {
                        this.calculateMaxPosition(element);
                    } else {
                        this.init = true;
                    }

                    // set current position
                    const browserWidth = this.document.body.clientWidth;
                    let slotInView = 8;

                    if (browserWidth < 768) {
                        slotInView = 6;
                    }

                    if (browserWidth < 576) {
                        slotInView = 4;
                    }

                    if (this.maxTotalSlots > 4) {
                        const diff = this.maxTotalSlots - slotInView;

                        this.initUlPosition = diff * ONE_SLOT_PX * -1;
                        this.ulCurrentPosition = this.initUlPosition;
                    } else {
                        this.initUlPosition = 0;
                        this.ulCurrentPosition = this.initUlPosition;
                    }

                    this.forceUlPositionEvent.emit(this.ulCurrentPosition);

                    // reset and check if next week is empty
                    this.nextWeekEmpty = false;
                    if (this.slotCollection.data.length === 0) {
                        const future = addDays(this.currentDate, 7);
                        this.apiEndpoints
                            .callPitchSlots(pitchId, future)
                            .subscribe((slotColl: SlotCollection) => {
                                if (slotColl.data.length === 0) {
                                    this.nextWeekEmpty = true;
                                }
                            });
                    }
                } else {
                    this.transferState.set(SLOTCOLLECTION, slotCollection);
                }
            });
    }

    private calculateCutOffPointFor12PM(): any {
        return getTotalSlotsBefore12PM(this.days[0].slots) * ONE_SLOT_PX * -1;
    }

    private populateDaysWithSlots(slotCollection: SlotCollection): void {
        this.days = [];
        const now = startDay(slotCollection);

        for (
            let dayIndex = 0;
            dayIndex <= this.numberOfDaysToCollect;
            dayIndex++
        ) {
            const currentDate = addDays(
                new Date(now.getFullYear(), now.getMonth(), now.getDate()),
                dayIndex
            );

            this.days.push(this.collectSlotsForCurrentDay(currentDate));
        }
    }

    private createHourDurationObj(slotCollection: SlotCollection): void {
        const now = startDay(slotCollection);

        for (let i = 0; i <= this.numberOfDaysToCollect; i++) {
            const createDay = addDays(
                new Date(now.getFullYear(), now.getMonth(), now.getDate()),
                i
            );

            this.dayTimeDuration[format(createDay, "yyyy-MM-dd")] = {};
        }

        slotCollection.data.map((slot: Slot) => {
            const slotStartTime = hoursMinutes(new Date(slot.attributes.starts));
            const date = format(new Date(slot.attributes.starts), "yyyy-MM-dd");
            const diff = diffInMinutes(
                new Date(slot.attributes.ends),
                new Date(slot.attributes.starts)
            );

            if (this.timeCollection.indexOf(slotStartTime) === -1)
                this.timeCollection.push(slotStartTime);
            if (!this.diffDurationsForTime[slotStartTime])
                this.diffDurationsForTime[slotStartTime] = [];
            if (this.diffDurationsForTime[slotStartTime].indexOf(diff) === -1) {
                this.diffDurationsForTime[slotStartTime].push(diff);
            }

            if (!this.dayTimeSlots[date]) this.dayTimeSlots[date] = {};

            if (
                this.dayTimeDuration[date] &&
                !this.dayTimeDuration[date][slotStartTime]
            ) {
                this.dayTimeDuration[date][slotStartTime] = [];
                this.dayTimeSlots[date][slotStartTime] = [];
            }

            if (
                this.dayTimeDuration[date] &&
                this.dayTimeDuration[date][slotStartTime].indexOf(diff) === -1
            ) {
                this.dayTimeDuration[date][slotStartTime].push(diff);
                this.dayTimeSlots[date][slotStartTime].push(slot);
                this.dayTimeSlots[date][slotStartTime].sort(compareSlots);

                if (
                    this.numberOfRecurringTimesInDay[slotStartTime] &&
                    this.numberOfRecurringTimesInDay[slotStartTime] <
                        this.dayTimeDuration[date][slotStartTime].length
                ) {
                    this.numberOfRecurringTimesInDay[slotStartTime] =
                        this.dayTimeDuration[date][slotStartTime].length;
                }

                if (!this.numberOfRecurringTimesInDay[slotStartTime]) {
                    this.numberOfRecurringTimesInDay[slotStartTime] =
                        this.dayTimeDuration[date][slotStartTime].length;
                }
            }
        });

        Object.keys(this.diffDurationsForTime).map((time) =>
            this.diffDurationsForTime[time].sort(compare)
        );
        this.timeCollection.sort(compare);
    }

    private collectSlotsForCurrentDay(currentDate: Date): {
        date: Date;
        slots: Array<Slot>;
    } {
        const slots = [];
        const currDay = format(currentDate, "yyyy-MM-dd");
        const dayWithData = this.dayTimeDuration[currDay];
        const dayWithSlots = this.dayTimeSlots[currDay];

        if (!isEmptyObj(dayWithData)) {
            this.timeCollection.map((time) => {
                const timeLength = this.numberOfRecurringTimesInDay[time];
                const durationArr = this.dayTimeDuration[currDay];
                const hasTime = dayWithSlots[time];

                if (!hasTime) {
                    for (let i = 0; i < timeLength; i++) {
                        createDummySlot(
                            slots,
                            currDay,
                            time,
                            this.diffDurationsForTime[time][i]
                        );
                    }
                }

                if (hasTime) {
                    for (let i = 0; i < timeLength; i++) {
                        const slot = dayWithSlots[time][i];
                        let hasSlotData;

                        if (slot) {
                            const duration = diffInMinutes(
                                new Date(slot.attributes.ends),
                                new Date(slot.attributes.starts)
                            );
                            hasSlotData = durationArr.length
                                ? durationArr.indexOf(duration)
                                : 0;

                            if (hasSlotData !== -1) slots.push(slot);
                        }

                        if (hasSlotData === -1 || !slot)
                            createDummySlot(
                                slots,
                                currDay,
                                time,
                                this.diffDurationsForTime[time][i]
                            );
                    }
                }
            });
        }

        if (isEmptyObj(dayWithData)) {
            this.timeCollection.map((time) => {
                const timeLength = this.numberOfRecurringTimesInDay[time];

                for (let i = 0; i < timeLength; i++) {
                    createDummySlot(
                        slots,
                        currDay,
                        time,
                        this.diffDurationsForTime[time][i]
                    );
                }
            });
        }

        return {
            date: currentDate,
            slots,
        };
    }

    private findMinTimeStep(slots: SlotCollection): void {
        let minTimeStep = 1000;

        slots.data.forEach((slot: Slot) => {
            const minutes = diffInMinutes(
                new Date(slot.attributes.ends),
                new Date(slot.attributes.starts)
            );
            if (minutes < minTimeStep) {
                minTimeStep = minutes;
            }
        });

        if (minTimeStep !== 1000) {
            this.minTimeStep = minTimeStep;
        }
    }
}

function getTotalSlotsBefore12PM(slots: Array<Slot>): number {
    let counter = 0;

    slots.forEach((slot: Slot) => {
        if (format(new Date(slot.attributes.starts), "HHmm") < "1200") {
            counter++;
        }
    });

    return counter;
}

function hoursMinutes(time): string {
    return format(time, "HHmm");
}

function isEmptyObj(map): boolean {
    for (const key in map) {
        if (map.hasOwnProperty(key)) {
            return false;
        }
    }

    return true;
}

function startDay(slotCollection): Date {
    return new Date(slotCollection.meta.filter.starts);
}

function compare(a, b): number {
    if (a < b) return -1;
    if (a > b) return 1;

    return 0;
}

function compareSlots(a, b): number {
    const aa = diffInMinutes(new Date(a.attributes.ends), new Date(a.attributes.starts));
    const bb = diffInMinutes(new Date(b.attributes.ends), new Date(b.attributes.starts));

    if (aa < bb) return -1;
    if (aa > bb) return 1;

    return 0;
}

function createDummySlot(slots, currDay, time, duration): void {
    const starts = new Date(`${currDay} ${time.replace(/(\d{2})(\d{2})/, '$1:$2')}`);
    const ends = addMinutes(starts, parseInt(duration, 10));
    const slotDummy = new Slot(0, "Empty", starts, ends);
    slots.push(slotDummy);
}
